import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Clock, Check } from "react-feather";
import {
  selectRecentlyViewed,
  selectInProgressCookingProgresses,
  clearRecentlyViewed,
  formatDuration,
  timeAgo,
  useT,
  DIFFICULTY_LABEL_KEYS,
  selectLocale,
  useAppDispatch,
  useAppSelector,
  type RecentlyViewedRecipe,
  type RootState,
} from "@reciperoll/core";
import { RecipeFilterBar, useRecipeFilter } from "../components/RecipeBarFilter";
import { RecipeImage } from "../components/RecipeImage";
import FavouriteButton from "../components/Favourites/FavouriteButton";

// Date buckets, mirroring the RN Recently Viewed screen.
type DateGroup = "Today" | "Yesterday" | "Last Week" | "Older";

// Date-group value → i18n key, so the section headers localise while the grouping
// key stays fixed.
const DATE_GROUP_KEYS: Record<DateGroup, string> = {
  Today: "recentlyViewed.today",
  Yesterday: "recentlyViewed.yesterday",
  "Last Week": "recentlyViewed.lastWeek",
  Older: "recentlyViewed.older",
};
const GROUP_ORDER: DateGroup[] = ["Today", "Yesterday", "Last Week", "Older"];

function getDateGroup(dateString: string): DateGroup {
  const itemDate = new Date(dateString);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfLastWeek = new Date(startOfToday);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

  if (itemDate >= startOfToday) return "Today";
  if (itemDate >= startOfYesterday) return "Yesterday";
  if (itemDate >= startOfLastWeek) return "Last Week";
  return "Older";
}

// One recently-viewed row — full-width, with a "time ago" stamp.
function RecentlyViewedRow({
  recipe,
  isCooking,
  isLast,
}: {
  recipe: RecentlyViewedRecipe;
  isCooking: boolean;
  isLast: boolean;
}) {
  const t = useT();
  const locale = useAppSelector(selectLocale);
  return (
    <Link
      to={`/recipe/${recipe.id}`}
      className={`group relative flex items-center gap-3.5 h-[84px] px-3 rounded-xl hover:bg-surface-2 transition-colors ${
        isLast ? "" : "border-b border-line rounded-none"
      }`}
    >
      {isCooking && (
        <span className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-terracotta" />
      )}

      <div className="relative shrink-0">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-terracotta-wash">
          <RecipeImage recipe={recipe} />
        </div>
        {recipe.cooked && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center border-2 border-chrome">
            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold text-ink mb-0.5 line-clamp-2 group-hover:text-terracotta-ink transition-colors">
          {recipe.title}
        </p>
        <p className="text-sm text-ink-soft flex items-center gap-1 truncate">
          {!!recipe.cookingDuration && (
            <>
              <Clock className="w-3 h-3 text-terracotta shrink-0" />
              {formatDuration(recipe.cookingDuration, t)} ·{" "}
            </>
          )}
          {recipe.difficulty ? `${t(DIFFICULTY_LABEL_KEYS[recipe.difficulty])} · ` : ""}
          {timeAgo(recipe.viewedAt, locale)}
        </p>
      </div>

      <FavouriteButton
        recipe={recipe}
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 hover:bg-surface-2 transition-colors"
      />
    </Link>
  );
}

// "See all" for the home Recently Viewed rail — every recipe you've opened,
// grouped by when you last viewed it (Today / Yesterday / Last Week / Older).
// Mirrors the RN screen: no sort/switcher (recency is the point), a filter bar,
// and a Clear action.
export default function RecentlyViewed() {
  const t = useT();
  const dispatch = useAppDispatch();
  const items = useAppSelector((s: RootState) => selectRecentlyViewed(s));
  const inProgress = useAppSelector(selectInProgressCookingProgresses);
  const cookingIds = useMemo(
    () => new Set(inProgress.map((cp) => cp.recipeId)),
    [inProgress],
  );

  // Filter pills applied before grouping by date.
  const { filters, activeKeys, toggle, clear, filteredRecipes } =
    useRecipeFilter(items);

  // Non-empty date groups, in order.
  const sections = useMemo(() => {
    const grouped: Record<DateGroup, RecentlyViewedRecipe[]> = {
      Today: [],
      Yesterday: [],
      "Last Week": [],
      Older: [],
    };
    filteredRecipes.forEach((r) => grouped[getDateGroup(r.viewedAt)].push(r));
    return GROUP_ORDER.filter((g) => grouped[g].length > 0).map((g) => ({
      title: g,
      data: grouped[g],
    }));
  }, [filteredRecipes]);

  const handleClear = () => {
    if (
      confirm(t("recentlyViewed.clearConfirmWeb"))
    ) {
      dispatch(clearRecentlyViewed());
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="font-display text-3xl font-extrabold text-ink">
          {t("home.recentlyViewed")}
        </h1>
        {items.length > 0 && (
          <button
            onClick={handleClear}
            className="text-sm font-semibold text-terracotta-ink hover:text-terracotta-dark cursor-pointer shrink-0"
          >
            {t("search.clear")}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-ink-faint text-sm">
          {t("recentlyViewed.emptyDescription")}
        </p>
      ) : (
        <>
          <RecipeFilterBar
            filters={filters}
            activeKeys={activeKeys}
            onToggle={toggle}
            onClear={clear}
            totalCount={items.length}
          />

          {sections.length === 0 ? (
            <p className="text-ink-faint text-sm">
              {t("recentlyViewed.noMatchFilters")}
            </p>
          ) : (
            <div className="flex flex-col gap-6">
              {sections.map((section) => (
                <div key={section.title}>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-faint mb-1.5">
                    {t(DATE_GROUP_KEYS[section.title])}
                  </p>
                  <div>
                    {section.data.map((recipe, i) => (
                      <RecentlyViewedRow
                        key={recipe.id}
                        recipe={recipe}
                        isCooking={cookingIds.has(recipe.id)}
                        isLast={i === section.data.length - 1}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
